import React, { useMemo, useState } from 'react';
import { Attachment, AttachmentObject } from '@eduact/ed-components';
import {
	imageTypes,
	docsTypes,
} from '@eduact/ed-components/DataDisplay/Attachment';
import { createTheme, ThemeProvider } from '@eduact/student-theme';
import { FlexLayout } from '@eduact/ed-system';
import styled from 'styled-components';
import { Dialog, IconButton } from '@mui/material';
import EdIcon from '@Components/UI/Utilities/EdIcon/EdIcon';
import { openAttachment, parseAttachmentAnswer } from '@ContentManagement/attachmentAnswer';
import { Unreadable } from '@ContentManagement/Unreadable';
type Props = {
	answer: string;
};
//components
//--
const theme = createTheme({}) as any;
const AttachmentContent: React.VoidFunctionComponent<Props> = ({ answer }) => {
	const { attachments, unreadable } = useMemo(
		() => parseAttachmentAnswer(answer),
		[answer]
	);

	const [isOpen, setIsOpen] = useState(false);
	const [attachment, setAttachment] = useState<AttachmentObject | undefined>();
	const [scale, setScale] = useState(1);
	const [rotation, setRotation] = useState(0);
	const handleOpenAttachment = (attachment: AttachmentObject) => {
		if (!imageTypes.includes(attachment.type) && attachment.type !== 'pdf') {
			openAttachment(attachment.url);
			return;
		}
		setAttachment(attachment);
		setScale(1);
		setRotation(0);
		setIsOpen(true);
	};
	const handleCloseAttachment = () => {
		setAttachment(undefined);
		setIsOpen(false);
	};
	return (
		<ThemeProvider theme={theme as any}>
			<Dialog onClose={handleCloseAttachment} maxWidth="xl" open={isOpen}>
				<>
					{attachment && (
						<div
							style={{
								width: '100%',
								height: '100%',
								display: 'flex',
								flexDirection: 'column',
								alignItems: 'center',
								overflow: 'hidden',
								position: 'relative',
							}}
						>
							<Toolbar>
								<ActionGroup>
									<IconButton
										onClick={() => setScale((prev) => prev + 0.25)}
										title="Zoom In"
									>
										<EdIcon>zoom_in</EdIcon>
									</IconButton>
									<IconButton
										onClick={() =>
											setScale((prev) => Math.max(0.5, prev - 0.25))
										}
										title="Zoom Out"
									>
										<EdIcon>zoom_out</EdIcon>
									</IconButton>
									<IconButton
										onClick={() => setRotation((prev) => prev + 90)}
										title="Rotate"
									>
										<EdIcon>rotate_right</EdIcon>
									</IconButton>
								</ActionGroup>
								<IconButton onClick={handleCloseAttachment} title="Close">
									<EdIcon>close</EdIcon>
								</IconButton>
							</Toolbar>
							<div
								style={{
									flex: 1,
									display: 'flex',
									justifyContent: 'center',
									alignItems: 'center',
									overflow: 'auto',
									width: '100%',
									height: '100%',
									position: 'relative',
								}}
							>
								{imageTypes.includes(attachment.type) && (
									<img
										src={attachment.url}
										alt=""
										style={{
											height: `${scale * 85}vh`,
											width: 'auto',
											maxWidth: 'none',
											maxHeight: 'none',
											objectFit: 'contain',
											transform: `rotate(${rotation}deg)`,
											transition: 'all 0.2s ease-in-out',
											transformOrigin: 'center center',
											margin: 'auto',
										}}
									/>
								)}
								{docsTypes.includes(attachment.type) && (
									<object
										style={{ width: '90vw', height: '90vh' }}
										data={attachment.url}
										datatype="application/pdf"
									>
										<iframe
											width={'100%'}
											height={'100%'}
											src={attachment.url}
											title="Pdf"
										></iframe>
									</object>
								)}
							</div>
						</div>
					)}
				</>
			</Dialog>
			<FlexLayout gridGap={'1rem'}>
				{attachments.map((attachment) => {
					return (
						<Attachment
							key={attachment.url}
							onClick={handleOpenAttachment}
							type={attachment.type as AttachmentObject['type']}
							url={attachment.url}
							withDelete={false}
						/>
					);
				})}
				{unreadable && <Unreadable>Some attachments could not be read.</Unreadable>}
			</FlexLayout>
		</ThemeProvider>
	);
};

export default AttachmentContent;


const Toolbar = styled.div`
	position: absolute;
	top: 1rem;
	left: 50%;
	transform: translateX(-50%);
	display: flex;
	align-items: center;
	gap: 1rem;
	z-index: 10;
	background: rgba(255, 255, 255, 0.9);
	padding: 0.5rem 1rem;
	border-radius: 2rem;
	box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
`;

const ActionGroup = styled.div`
	display: flex;
	align-items: center;
	gap: 0.5rem;
	padding-right: 1rem;
	border-right: 1px solid #ddd;
`;
